import { useCallback, useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { useLocaleStore } from '@/lib/core/stores/useLocaleStore';
import { useActiveRideStore } from '@/lib/core/stores/useActiveRideStore';
import { listRides } from '@/services/platform/data/booking/rides';
import {
  isRideInProgress,
  isRideConfirmationReady,
  isRideCancelled,
} from '@/services/platform/data/booking/rides/rideStatus';
import { useRideUpdates } from '@/services/platform/data/booking/rides/useRideUpdates';

// How long to keep a terminal ride in the store before untracking it — long enough
// for a mounted `RangeRoverDriverInfo` to observe the final status itself (advance
// to confirmation, or show the cancelled notice) and clear it immediately on its
// own; this is only the backstop for when nothing was mounted to react (e.g. the
// user was on Home when the ride finished).
const TERMINAL_RIDE_CLEAR_DELAY_MS = 10000;

/**
 * Owns the app's single live ride-tracking connection, mounted once at the app
 * root (alongside `useDeepLink`/`useCartCountSync`) so it survives navigation
 * away from the driver-info screen. Seeds `useActiveRideStore` from a REST check
 * on cold start / app resume (covers relaunch after the app was fully closed —
 * a live SignalR connection can't itself survive the OS suspending/killing the
 * WebView in the background), then keeps it fresh via `useRideUpdates` for as
 * long as an active ride exists.
 */
export function useActiveRideTracking() {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const locale = useLocaleStore(state => state.locale);
  const publicGuid = useActiveRideStore(state => state.publicGuid);
  const setActiveRide = useActiveRideStore(state => state.setActiveRide);
  const updateRide = useActiveRideStore(state => state.updateRide);
  const clearActiveRide = useActiveRideStore(state => state.clearActiveRide);

  const checkForInProgressRide = useCallback(async () => {
    if (!isAuthenticated) return;
    const rides = await listRides(locale);
    const active = rides?.find(ride => isRideInProgress(ride.status) && !ride.pickupTime);
    if (active) {
      setActiveRide(active);
    } else {
      clearActiveRide();
    }
  }, [isAuthenticated, locale, setActiveRide]);

  // Cold start / auth-ready: discover an already-in-progress ride (app relaunch case).
  useEffect(() => {
    checkForInProgressRide();
  }, [checkForInProgressRide]);

  // App resume from background — re-check in case anything changed while backgrounded.
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listenerPromise = App.addListener('resume', () => {
      checkForInProgressRide();
    });

    return () => {
      listenerPromise.then(listener => listener.remove());
    };
  }, [checkForInProgressRide]);

  // The one live subscription — only connects while `publicGuid` is set, and keeps
  // running for as long as this hook is mounted (i.e. the whole app session).
  const { ride } = useRideUpdates(publicGuid ?? undefined);

  // Always write the update through, even for a terminal status — consumers (e.g.
  // `RangeRoverDriverInfo`) need to observe that final status themselves (to advance
  // to confirmation, or show the cancelled notice) before the ride is untracked.
  // Clearing here instead would wipe it out in the same render pass, so nothing
  // downstream ever actually sees the terminal state.
  useEffect(() => {
    if (!ride) return;
    updateRide(ride);

    // Backstop: untrack it shortly after regardless of whether anything reacted
    // (`RangeRoverDriverInfo` also clears it itself, immediately, when mounted).
    if (isRideConfirmationReady(ride.status) || isRideCancelled(ride.status)) {
      const timer = setTimeout(clearActiveRide, TERMINAL_RIDE_CLEAR_DELAY_MS);
      return () => clearTimeout(timer);
    }
  }, [ride, updateRide, clearActiveRide]);
}
